import { useEffect, useState } from 'react';
import { useAuth } from '../hooks/useAuth';
import { useApi } from '../hooks/useApi';
import { useAnimatedNumber } from '../hooks/useAnimatedNumber';
import { useLevels } from '../hooks/useLevels';
import { iconFor, getLevelForScore, getNextLevel, getLevelProgress } from '../utils/levels';
import { Link } from 'react-router-dom';
import {
  Gamepad2, Users, Medal, User, Lightbulb,
  ChevronRight, MapPin,
} from 'lucide-react';
import { avatarSrc } from '../components/avatar/avatarList';
import RanksDialog from '../components/ui/RanksDialog';
import './DashboardPage.css';

const DashboardPage = () => {
  const { user } = useAuth();
  const { request, loading } = useApi();
  const [stats, setStats] = useState(null);
  const [ranksOpen, setRanksOpen] = useState(false);

  useEffect(() => {
    request('get', '/game/stats').then(setStats).catch(console.error);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Prefer the always-fresh /game/stats response; the auth `user` object is only
  // a login-time snapshot and is never refreshed after a game, so it would show
  // stale (often 0) values here. Fall back to `user` until stats finish loading.
  const totalScore   = stats?.stats?.total_score  ?? user?.total_score   ?? 0;
  const gamesPlayed  = stats?.stats?.total_games  ?? user?.games_played  ?? 0;
  const bestScore    = stats?.stats?.best_score   ?? user?.best_score    ?? 0;
  const avgScore     = stats?.stats?.average_score || 0;

  const animTotal  = useAnimatedNumber(totalScore,         { duration: 1500 });
  const animGames  = useAnimatedNumber(gamesPlayed,        { duration: 1500 });
  const animBest   = useAnimatedNumber(bestScore,          { duration: 1500 });
  const animAvg    = useAnimatedNumber(Math.round(avgScore), { duration: 1500 });

  const { levels } = useLevels();
  const rank      = getLevelForScore(levels, totalScore) ?? { name: '—', color: '#64748b', icon: 'trophy' };
  const nextRank  = getNextLevel(levels, totalScore);
  const progress  = getLevelProgress(levels, totalScore);
  const RankIcon  = iconFor(rank.icon);
  const NextIcon  = nextRank ? iconFor(nextRank.icon) : null;
  const firstName = user?.name?.split(' ')[0] || 'Explorer';

  if (loading && !user) {
    return (
      <div className="dash-loading">
        <div className="dash-spinner" />
      </div>
    );
  }

  return (
    <div className="dash">
      <div className="dash-container">

        {/* ── Hero ── */}
        <div className="dash-hero">
          <div className="dash-hero-body">
            <div className="dash-avatar" style={{ borderColor: rank.color }}>
              {user?.avatar
                ? <img src={avatarSrc(user.avatar)} alt="" />
                : user?.name?.charAt(0).toUpperCase()}
            </div>
            <div className="dash-hero-text">
              <p className="dash-welcome">Welcome back</p>
              <h1 className="dash-name">{firstName}</h1>
              <button
                type="button"
                className="dash-rank-badge rd-trigger"
                style={{ color: rank.color }}
                onClick={() => setRanksOpen(true)}
                aria-label={`Current rank ${rank.name}. View all rank tiers.`}
              >
                <RankIcon size={13} />
                {rank.name}
              </button>
            </div>
          </div>
          <Link to="/game" className="dash-play-btn">
            <Gamepad2 size={18} />
            Play Now
          </Link>
        </div>

        {/* ── Stats ── */}
        <div className="dash-stats">
          <div className="dash-stat">
            <span className="dash-stat-num">{animTotal.toLocaleString()}</span>
            <span className="dash-stat-label">Total Score</span>
          </div>
          <div className="dash-stat-sep" />
          <div className="dash-stat">
            <span className="dash-stat-num">{animGames}</span>
            <span className="dash-stat-label">Games</span>
          </div>
          <div className="dash-stat-sep" />
          <div className="dash-stat">
            <span className="dash-stat-num">{animBest.toLocaleString()}</span>
            <span className="dash-stat-label">Best Score</span>
          </div>
          <div className="dash-stat-sep" />
          <div className="dash-stat">
            <span className="dash-stat-num">{animAvg.toLocaleString()}</span>
            <span className="dash-stat-label">Avg Score</span>
          </div>
        </div>

        {/* ── Rank progress ── */}
        <div className="dash-rank-progress">
          <div className="dash-rank-progress-labels">
            <button
              type="button"
              className="rd-trigger"
              style={{ color: rank.color, display: 'inline-flex', alignItems: 'center', gap: 5, background: 'transparent', border: 0, padding: 0, font: 'inherit' }}
              onClick={() => setRanksOpen(true)}
              aria-label={`Current rank ${rank.name}. View all rank tiers.`}
            >
              <RankIcon size={12} /> {rank.name}
            </button>
            {nextRank && NextIcon && (
              <span className="dash-rank-next">
                <NextIcon size={12} style={{ color: nextRank.color }} />
                {nextRank.name} · {(nextRank.min_points - totalScore).toLocaleString()} pts to go
              </span>
            )}
          </div>
          <div className="dash-rank-bar">
            <div
              className="dash-rank-fill"
              style={{ width: `${progress}%`, background: rank.color }}
            />
          </div>
        </div>

        {/* ── Bento grid ── */}
        <div className="dash-bento">

          {/* Solo Game — featured */}
          <Link to="/game" className="dash-bento-card dash-card-solo">
            <div className="dash-card-solo-overlay" />
            <div className="dash-card-solo-body">
              <div className="dash-card-tag">
                <MapPin size={11} /> Nepal
              </div>
              <h2 className="dash-card-solo-title">Solo Game</h2>
              <p className="dash-card-solo-sub">5 rounds · Street View guessing</p>
              <div className="dash-card-solo-cta">
                Play <ChevronRight size={15} />
              </div>
            </div>
          </Link>

          {/* Multiplayer */}
          <Link to="/multiplayer" className="dash-bento-card dash-card-action dash-card-multi">
            <div className="dash-action-icon" style={{ background: 'rgba(59,130,246,0.12)', borderColor: 'rgba(59,130,246,0.2)', color: '#60a5fa' }}>
              <Users size={20} />
            </div>
            <div className="dash-action-info">
              <h3>Multiplayer</h3>
              <p>Challenge friends in real-time</p>
            </div>
            <ChevronRight size={16} className="dash-action-arrow" />
          </Link>

          {/* Leaderboard */}
          <Link to="/leaderboard" className="dash-bento-card dash-card-action dash-card-leader">
            <div className="dash-action-icon" style={{ background: 'rgba(245,158,11,0.12)', borderColor: 'rgba(245,158,11,0.2)', color: '#fbbf24' }}>
              <Medal size={20} />
            </div>
            <div className="dash-action-info">
              <h3>Leaderboard</h3>
              <p>See the top players</p>
            </div>
            <ChevronRight size={16} className="dash-action-arrow" />
          </Link>

          {/* Profile */}
          <Link to="/profile" className="dash-bento-card dash-card-action dash-card-profile">
            <div className="dash-action-icon" style={{ background: 'rgba(139,92,246,0.12)', borderColor: 'rgba(139,92,246,0.2)', color: '#a78bfa' }}>
              <User size={20} />
            </div>
            <div className="dash-action-info">
              <h3>Profile</h3>
              <p>View game history</p>
            </div>
            <ChevronRight size={16} className="dash-action-arrow" />
          </Link>

        </div>

        {/* ── Tip ── */}
        <div className="dash-tip">
          <Lightbulb size={16} className="dash-tip-icon" />
          <p>Look for road signs, shop names, and architecture styles. Kathmandu Valley has Newari style, the Himalayas have mountain architecture, and Terai has its own distinct feel.</p>
        </div>

      </div>

      <RanksDialog
        open={ranksOpen}
        onClose={() => setRanksOpen(false)}
        currentScore={totalScore}
      />
    </div>
  );
};

export default DashboardPage;
